import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTrips } from "@/hooks/useTrips";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Loader2, LogOut, Shield } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { TripDisplay } from "@/lib/trips";

interface TripFormData {
  title: string;
  destination: string;
  description: string;
  price: number;
  duration: string;
  image: string;
  category: string;
  group_size: string;
  rating: number;
  highlights: string;
  availability_status: string;
  itinerary: string;
}

const defaultFormData: TripFormData = {
  title: "",
  destination: "",
  description: "",
  price: 0,
  duration: "",
  image: "",
  category: "",
  group_size: "",
  rating: 4.5,
  highlights: "",
  availability_status: "available",
  itinerary: "",
};

const Admin = () => {
  const { user, isAdmin, isLoading: authLoading, signOut } = useAuth();
  const { data: trips, isLoading } = useTrips();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<TripDisplay | null>(null);
  const [formData, setFormData] = useState<TripFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">You don't have admin privileges.</p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatImageUrl = (url: string): string => {
    if (!url) return url;
    url = url.trim();
    // If URL doesn't start with http:// or https://, add https://
    if (!url.match(/^https?:\/\//i)) {
      // Check if it looks like a domain (contains a dot)
      if (url.includes('.') && !url.startsWith('/')) {
        return `https://${url}`;
      }
    }
    return url;
  };

  const validateImageUrl = (url: string): boolean => {
    if (!url) return false;
    try {
      const formattedUrl = formatImageUrl(url);
      const urlObj = new URL(formattedUrl);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleImageUrlChange = (url: string) => {
    setFormData({ ...formData, image: url });
    setImageError(false);
    setImagePreview(null);
    
    if (url) {
      const formattedUrl = formatImageUrl(url);
      if (validateImageUrl(url)) {
        // Test if image loads
        const img = new Image();
        img.onload = () => {
          setImagePreview(formattedUrl);
          setImageError(false);
          // Update form data with formatted URL if it was auto-formatted
          if (formattedUrl !== url) {
            setFormData({ ...formData, image: formattedUrl });
          }
        };
        img.onerror = () => {
          setImageError(true);
          setImagePreview(null);
        };
        img.src = formattedUrl;
      } else {
        setImageError(true);
      }
    }
  };

  const handleOpenDialog = (trip?: TripDisplay) => {
    if (trip) {
      setEditingTrip(trip);
      setFormData({
        title: trip.title,
        destination: trip.destination,
        description: trip.description,
        price: trip.price,
        duration: trip.duration,
        image: trip.image,
        category: trip.category,
        group_size: trip.group_size,
        rating: trip.rating,
        highlights: trip.highlights.join(", "),
        availability_status: trip.availability_status || "available",
        itinerary: trip.itinerary || "",
      });
      // Test existing image
      if (trip.image) {
        handleImageUrlChange(trip.image);
      }
    } else {
      setEditingTrip(null);
      setFormData(defaultFormData);
      setImagePreview(null);
      setImageError(false);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const tripData = {
      title: formData.title,
      destination: formData.destination,
      description: formData.description,
      price: formData.price,
      duration: formData.duration,
      image: formData.image,
      category: formData.category,
      group_size: formData.group_size,
      rating: formData.rating,
      highlights: formData.highlights.split(",").map((h) => h.trim()).filter(Boolean),
      availability_status: formData.availability_status,
      itinerary: formData.itinerary || null,
    };

    try {
      if (editingTrip) {
        const { error } = await supabase
          .from("trips")
          .update(tripData)
          .eq("id", editingTrip.id);

        if (error) throw error;
        toast.success("Trip updated successfully!");
      } else {
        const { error } = await supabase.from("trips").insert([tripData]);

        if (error) throw error;
        toast.success("Trip created successfully!");
      }

      queryClient.invalidateQueries({ queryKey: ["trips"] });
      setIsDialogOpen(false);
      setFormData(defaultFormData);
      setEditingTrip(null);
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (tripId: string) => {
    if (!confirm("Are you sure you want to delete this trip?")) return;

    try {
      const { error } = await supabase.from("trips").delete().eq("id", tripId);

      if (error) throw error;
      toast.success("Trip deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-display text-xl font-semibold">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user.email}</span>
            <Button variant="ghost" size="icon" onClick={handleSignOut}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Manage Trips</CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Trip
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTrip ? "Edit Trip" : "Add New Trip"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="destination">Destination</Label>
                      <Input
                        id="destination"
                        value={formData.destination}
                        onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">Price (₹)</Label>
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration</Label>
                      <Input
                        id="duration"
                        placeholder="e.g., 5 Days"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rating">Rating</Label>
                      <Input
                        id="rating"
                        type="number"
                        step="0.1"
                        min="1"
                        max="5"
                        value={formData.rating}
                        onChange={(e) => setFormData({ ...formData, rating: Number(e.target.value) })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Input
                        id="category"
                        placeholder="e.g., Adventure & Nature"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="group_size">Group Size</Label>
                      <Input
                        id="group_size"
                        placeholder="e.g., 10-14 travelers"
                        value={formData.group_size}
                        onChange={(e) => setFormData({ ...formData, group_size: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image">Image URL</Label>
                    <Input
                      id="image"
                      type="url"
                      value={formData.image}
                      onChange={(e) => handleImageUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      required
                      className={imageError && formData.image ? "border-destructive" : ""}
                    />
                    {imageError && formData.image && (
                      <p className="text-sm text-destructive">
                        Invalid URL or image failed to load. Please check the URL.
                      </p>
                    )}
                    {imagePreview && (
                      <div className="mt-2">
                        <p className="text-sm text-muted-foreground mb-2">Preview:</p>
                        <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                          />
                        </div>
                      </div>
                    )}
                    {!imagePreview && formData.image && !imageError && (
                      <p className="text-sm text-muted-foreground">Loading preview...</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="highlights">Highlights (comma-separated)</Label>
                    <Input
                      id="highlights"
                      placeholder="e.g., Beach, Hiking, Culture"
                      value={formData.highlights}
                      onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="availability_status">Availability Status</Label>
                    <Select
                      value={formData.availability_status}
                      onValueChange={(value) => setFormData({ ...formData, availability_status: value })}
                    >
                      <SelectTrigger id="availability_status">
                        <SelectValue placeholder="Select availability status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="few_slots">Few Slots Left</SelectItem>
                        <SelectItem value="fully_booked">Fully Booked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="itinerary">Itinerary</Label>
                    <Textarea
                      id="itinerary"
                      placeholder="Enter day-by-day itinerary with times and activities. Example:&#10;Day 1: Friday – The Speed Run & Sunset Vibes&#10;● 06:00 AM | Depart Delhi&#10;○ Enter the Delhi-Mumbai Expressway..."
                      value={formData.itinerary}
                      onChange={(e) => setFormData({ ...formData, itinerary: e.target.value })}
                      rows={15}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use "Day X:" for day headers, "●" for main activities, and "○" for sub-activities.
                    </p>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingTrip ? "Update Trip" : "Create Trip"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trips?.map((trip) => (
                      <TableRow key={trip.id}>
                        <TableCell className="font-medium">{trip.title}</TableCell>
                        <TableCell>{trip.destination}</TableCell>
                        <TableCell>₹{trip.price.toLocaleString("en-IN")}</TableCell>
                        <TableCell>{trip.duration}</TableCell>
                        <TableCell>{trip.rating}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(trip)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(trip.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Admin;
